import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function PurchaseOrderNew() {
  const [form, setForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    status: 'draft',
    google_sheet_url: '',
    notes: '',
  })
  const [processing, setProcessing] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post('/purchase_orders', { purchase_order: form }, {
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button asChild variant="ghost" size="icon-sm">
          <Link href="/purchase_orders">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            New Purchase Order
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create a new purchase order
          </p>
        </div>
      </div>

      <Card className="max-w-2xl border-border/50">
        <CardHeader>
          <CardTitle className="text-base">Purchase Order Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => setForm({ ...form, status: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="estimated">Estimated</SelectItem>
                    <SelectItem value="finalized">Finalized</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="google_sheet_url">Google Sheet URL</Label>
              <Input
                id="google_sheet_url"
                type="url"
                placeholder="https://docs.google.com/spreadsheets/..."
                value={form.google_sheet_url}
                onChange={(e) => setForm({ ...form, google_sheet_url: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any relevant notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create Purchase Order'}
              </Button>
              <Button asChild variant="ghost">
                <Link href="/purchase_orders">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AppLayout>
  )
}
